import { useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useThemeColors } from '../hooks/useThemeColors';
import { trackTermsAccepted } from '../lib/analytics';

const TERMS_KEY = 'primate-terms-accepted';

interface TermsModalProps {
  onAccept: () => void;
  /**
   * Stacking context for the overlay. Defaults to TERMS_MODAL_Z (10000) so the
   * consent gate always renders above the mobile tabbar (z=100), streaming HUD
   * (z=150), and input-bar menus (z=200-300). Callers can override but should
   * not go below TERMS_MODAL_Z.
   */
  zIndex?: number;
}

// Dedicated top layer for the consent gate. Must sit above every interactive
// mobile surface (tabbar 100, HUD 150, V2InputBar menus 200-300).
export const TERMS_MODAL_Z = 10000;

export default function TermsModal({ onAccept, zIndex = TERMS_MODAL_Z }: TermsModalProps) {
  const c = useThemeColors();
  const isMobile = useIsMobile();
  const [checked, setChecked] = useState(false);

  function handleAccept() {
    if (!checked) return;
    localStorage.setItem(TERMS_KEY, new Date().toISOString());
    trackTermsAccepted();
    onAccept();
  }

  return (
    <div
      data-testid="terms-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        // On mobile: pad top 16px and bottom for tab bar + safe area so the card + button are always visible
        padding: isMobile ? '16px 16px calc(49px + env(safe-area-inset-bottom) + 16px)' : '24px',
        overflowY: isMobile ? 'auto' : 'visible',
      }}
    >
      <div
        style={{
          background: c.bgSecondary,
          border: `1px solid ${c.border}`,
          borderRadius: isMobile ? 12 : 4,
          padding: isMobile ? '24px 20px' : 32,
          maxWidth: 560,
          width: '100%',
          // On mobile: let the card grow naturally; the overlay scrolls if needed
          flexShrink: 0,
        }}
      >
        {/* Headline */}
        <h2 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 24, color: c.textPrimary, margin: '0 0 10px' }}>
          Welcome to Primate Vision
        </h2>

        {/* Subtitle */}
        <p style={{ fontSize: 14, color: c.textMuted, margin: '0 0 24px', lineHeight: '1.55' }}>
          Before you run your first analysis, please review how we handle your videos.
        </p>

        {/* Scrollable content */}
        <div
          style={{
            maxHeight: isMobile ? 180 : 320,
            overflowY: 'auto',
            marginBottom: 24,
            paddingRight: 8,
          }}
        >
          {/* Section: What we do with your videos */}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, margin: '0 0 10px', letterSpacing: '-0.01em' }}>
            What we do with your videos
          </h3>
          <ul style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.65', paddingLeft: 18, margin: '0 0 20px' }}>
            <li>
              <strong style={{ color: c.textPrimary }}>Processing:</strong> your video is processed on our secure AWS servers to answer your query
            </li>
            <li>
              <strong style={{ color: c.textPrimary }}>Retention:</strong> videos you save to your library, session recordings, and analysis results are retained until you delete them from your dashboard
            </li>
            <li>
              <strong style={{ color: c.textPrimary }}>Training data:</strong> By using Primate Vision, you agree that anonymised
              analysis results (not your original video) may be used to improve our models — we never use raw video for
              training without explicit separate consent.
            </li>
            <li>
              <strong style={{ color: c.textPrimary }}>No sharing:</strong> we don&apos;t share your videos or results with any third party, except as required by law
            </li>
          </ul>

          {/* Section: What you agree to */}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, margin: '0 0 10px', letterSpacing: '-0.01em' }}>
            What you agree to
          </h3>
          <ul style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.65', paddingLeft: 18, margin: '0 0 20px' }}>
            <li>You have the right to upload the videos you submit</li>
            <li>You won&apos;t upload illegal content, private individuals without consent, or content violating anyone&apos;s rights</li>
            <li>You understand this is a preview product — results may be inaccurate and carry no guarantee of fitness for any purpose.</li>
          </ul>

          {/* Section: Your rights */}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, margin: '0 0 10px', letterSpacing: '-0.01em' }}>
            Your rights
          </h3>
          <ul style={{ color: c.textMuted, fontSize: 13, lineHeight: '1.65', paddingLeft: 18, margin: 0 }}>
            <li>
              Request deletion of your account and all data anytime via{' '}
              <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
                privacy@primateintelligence.ai
              </a>
              , processed within 30 days
            </li>
            <li>
              Full documentation at{' '}
              <a href="/terms" style={{ color: c.signal, textDecoration: 'none' }}>primateintelligence.ai/terms</a>
              {' '}and{' '}
              <a href="/privacy" style={{ color: c.signal, textDecoration: 'none' }}>primateintelligence.ai/privacy</a>
            </li>
          </ul>
        </div>

        {/* Checkbox */}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            cursor: 'pointer',
            marginBottom: 20,
          }}
        >
          <input
            type="checkbox"
            data-testid="terms-accept-checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{
              width: 16,
              height: 16,
              marginTop: 2,
              accentColor: c.signal,
              flexShrink: 0,
              cursor: 'pointer',
            }}
          />
          <span style={{ fontSize: 13, color: c.textPrimary, lineHeight: '1.55' }}>
            I have read and agree to the Terms of Use and Privacy Policy
          </span>
        </label>

        {/* CTA Button */}
        <button
          data-testid="terms-accept-btn"
          onClick={handleAccept}
          disabled={!checked}
          style={{
            width: '100%',
            background: c.signal,
            opacity: checked ? 1 : 0.4,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 3,
            padding: '13px 20px',
            fontSize: 14,
            fontWeight: 700,
            cursor: checked ? 'pointer' : 'not-allowed',
            transition: 'opacity 150ms',
            letterSpacing: '0.01em',
            marginBottom: 12,
          }}
        >
          Get started
        </button>

        {/* Footer text */}
        <p style={{ textAlign: 'center', fontSize: 12, color: c.textMuted, margin: 0 }}>
          Questions? Email{' '}
          <a href="mailto:privacy@primateintelligence.ai" style={{ color: c.signal, textDecoration: 'none' }}>
            privacy@primateintelligence.ai
          </a>
        </p>
      </div>
    </div>
  );
}
